import { isOverlayId, type OverlayId, type TabId } from './destinations';

// URL → layer classification (the route map in contract §4). Pure, so the
// navigator reducer and the redirect rules can be unit-tested.

export type PushedPageId =
  | 'section'
  | 'browse'
  | 'series'
  | 'collection'
  | 'author'
  | 'settings'
  | 'storageDownloads'
  | 'generalConnections'
  | 'connections'
  | 'onboarding';

export type ModalId = 'book' | 'player';
export type GateId = 'welcome' | 'no-connections';

export type RouteClass =
  | { kind: 'tab'; tab: TabId }
  | { kind: 'overlay'; overlay: OverlayId }
  | { kind: 'pushed'; page: PushedPageId }
  | { kind: 'modal'; modal: ModalId }
  | { kind: 'gate'; gate: GateId }
  | { kind: 'dev' }
  | { kind: 'redirect'; to: string }
  | { kind: 'unknown' };

export type StackRouteClass = Extract<RouteClass, { kind: 'pushed' | 'modal' }>;
export type BaseRouteClass = Extract<RouteClass, { kind: 'tab' | 'overlay' }>;

/** The parts of a router Location the classification reads. */
export interface LocationLike {
  pathname: string;
  search?: string;
  hash?: string;
  state?: unknown;
  key?: string;
}

export interface ClassifyOptions {
  signedIn: boolean;
  /** Dev builds expose `/dev/gallery`. */
  dev?: boolean;
}

const BROWSE_SECTIONS = new Set(['series', 'authors']);

function stateRecord(state: unknown): Record<string, unknown> | null {
  return state && typeof state === 'object' ? (state as Record<string, unknown>) : null;
}

/** `nav.push()` marks the locations whose path alone is ambiguous (`/settings`, `/welcome`). */
export function isPushedState(state: unknown): boolean {
  return stateRecord(state)?.pushed === true;
}

/** `location.state.background` set by `nav.openBook` / `nav.openPlayer`. */
export function backgroundOf(loc: LocationLike): LocationLike | null {
  const bg = stateRecord(loc.state)?.background;
  const record = stateRecord(bg);
  return record && typeof record.pathname === 'string' ? (record as unknown as LocationLike) : null;
}

export function normalizePathname(pathname: string): string {
  const trimmed = pathname.replace(/\/{2,}/g, '/');
  return trimmed.length > 1 && trimmed.endsWith('/') ? trimmed.slice(0, -1) : trimmed || '/';
}

function segments(pathname: string): string[] {
  return normalizePathname(pathname).split('/').filter(Boolean);
}

const SEARCH_SECTIONS = new Set(['books', 'series', 'collections', 'authors']);

/**
 * `/search[?q=…][&section=…]` (PWA-only link): the Library tab with its search
 * panel open and focused, pre-filled with `q` (spec 03 §3; the hub drops the
 * one-shot parameters once applied).
 */
export function librarySearchTarget(search: string | undefined): string {
  const from = new URLSearchParams(search ?? '');
  const params = new URLSearchParams({ search: '1' });
  const q = from.get('q');
  const section = from.get('section');
  if (q) params.set('q', q);
  if (section && SEARCH_SECTIONS.has(section)) params.set('section', section);
  return `/library?${params.toString()}`;
}

export function classifyLocation(loc: LocationLike, { signedIn, dev = false }: ClassifyOptions): RouteClass {
  const parts = segments(loc.pathname);
  const [first, second, third] = parts;
  const count = parts.length;

  switch (first) {
    case undefined:
      return { kind: 'tab', tab: 'home' };
    case 'home':
      if (count === 1) return { kind: 'tab', tab: 'home' };
      if (second === 'section' && count === 3) return { kind: 'pushed', page: 'section' };
      return { kind: 'unknown' };
    case 'library':
      return count === 1 ? { kind: 'tab', tab: 'library' } : { kind: 'unknown' };
    case 'downloads':
      return count === 1 ? { kind: 'tab', tab: 'downloads' } : { kind: 'unknown' };
    case 'menu':
      if (count === 1) return { kind: 'tab', tab: 'menu' };
      if (count === 2 && isOverlayId(second)) return { kind: 'overlay', overlay: second };
      return { kind: 'redirect', to: '/menu' };
    case 'settings':
      if (count === 1) return isPushedState(loc.state) ? { kind: 'pushed', page: 'settings' } : { kind: 'tab', tab: 'settings' };
      if (count === 3 && second === 'storage' && third === 'downloads') return { kind: 'pushed', page: 'storageDownloads' };
      if (count === 3 && second === 'general' && third === 'connections') return { kind: 'pushed', page: 'generalConnections' };
      return { kind: 'pushed', page: 'settings' };
    case 'connections':
      return count >= 2 ? { kind: 'pushed', page: 'connections' } : { kind: 'redirect', to: '/menu/connections' };
    case 'browse':
      return count === 2 && second && BROWSE_SECTIONS.has(second) ? { kind: 'pushed', page: 'browse' } : { kind: 'unknown' };
    case 'series':
      return count === 2 ? { kind: 'pushed', page: 'series' } : { kind: 'unknown' };
    case 'collection':
      return count === 2 ? { kind: 'pushed', page: 'collection' } : { kind: 'unknown' };
    case 'author':
      return count === 2 ? { kind: 'pushed', page: 'author' } : { kind: 'unknown' };
    case 'search':
      return count === 1 ? { kind: 'redirect', to: librarySearchTarget(loc.search) } : { kind: 'unknown' };
    case 'book':
    case 'item':
      return count === 2 ? { kind: 'modal', modal: 'book' } : { kind: 'unknown' };
    case 'player':
      return count === 1 ? { kind: 'modal', modal: 'player' } : { kind: 'unknown' };
    case 'stats':
      return count === 1 ? { kind: 'redirect', to: '/menu/statistics' } : { kind: 'unknown' };
    case 'welcome': {
      // Signed in, onboarding is only reachable as a pushed page (Home ⋮ "Back to Onboarding").
      const pushed = isPushedState(loc.state) || new URLSearchParams(loc.search ?? '').has('from');
      return signedIn && pushed ? { kind: 'pushed', page: 'onboarding' } : { kind: 'gate', gate: 'welcome' };
    }
    case 'no-connections':
      return count === 1 ? { kind: 'gate', gate: 'no-connections' } : { kind: 'unknown' };
    case 'dev':
      return dev && second === 'gallery' && count === 2 ? { kind: 'dev' } : { kind: 'unknown' };
    default:
      return { kind: 'unknown' };
  }
}

export function isStackRoute(route: RouteClass): route is StackRouteClass {
  return route.kind === 'pushed' || route.kind === 'modal';
}

export function isBaseRoute(route: RouteClass): route is BaseRouteClass {
  return route.kind === 'tab' || route.kind === 'overlay';
}

/** Pushed pages a signed-out user may open on top of a gate (Manage / Add Connection). */
export function isGateReachable(route: RouteClass): boolean {
  return (
    route.kind === 'pushed' &&
    (route.page === 'settings' || route.page === 'storageDownloads' || route.page === 'generalConnections' || route.page === 'connections')
  );
}

/** Which tab a base route shows (overlays live on the Menu tab). */
export function tabOfBase(route: BaseRouteClass): TabId {
  return route.kind === 'tab' ? route.tab : 'menu';
}

export type GateDecision = { kind: 'render' } | { kind: 'redirect'; to: string; state?: unknown };

export interface GateInput {
  route: RouteClass;
  location: LocationLike;
  /** The gate the session state asks for, or null when signed in. */
  gate: GateId | null;
}

const GATE_PATHS: Record<GateId, string> = { welcome: '/welcome', 'no-connections': '/no-connections' };

export function gatePath(gate: GateId): string {
  return GATE_PATHS[gate];
}

function returnTarget(location: LocationLike): LocationLike | null {
  const from = stateRecord(location.state)?.from;
  const record = stateRecord(from);
  return record && typeof record.pathname === 'string' ? (record as unknown as LocationLike) : null;
}

/**
 * AuthWrapper routing as URL redirects (spec 01 §5.4, 10 §14.1): signed out →
 * the gate (remembering where the user was going); signed in → never a gate.
 */
export function decideGate({ route, location, gate }: GateInput): GateDecision {
  if (route.kind === 'redirect') return { kind: 'redirect', to: route.to };
  if (route.kind === 'dev') return { kind: 'render' };

  if (gate) {
    if (route.kind === 'gate') return route.gate === gate ? { kind: 'render' } : { kind: 'redirect', to: gatePath(gate) };
    if (isGateReachable(route)) return { kind: 'render' };
    const remember = route.kind !== 'unknown' && !(route.kind === 'tab' && route.tab === 'home');
    return { kind: 'redirect', to: gatePath(gate), state: remember ? { from: stripTransient(location) } : undefined };
  }

  if (route.kind === 'unknown') return { kind: 'redirect', to: '/' };
  if (route.kind === 'gate') {
    const target = returnTarget(location);
    return target ? { kind: 'redirect', to: hrefOf(target), state: target.state } : { kind: 'redirect', to: '/' };
  }
  return { kind: 'render' };
}

export function hrefOf(loc: LocationLike): string {
  return `${loc.pathname}${loc.search ?? ''}${loc.hash ?? ''}`;
}

function stripTransient(loc: LocationLike): LocationLike {
  return { pathname: loc.pathname, search: loc.search ?? '', hash: loc.hash ?? '', state: loc.state ?? null };
}
